// =============================================================================
// MeuDriver — Landing Hero (Section 1)
// =============================================================================

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Search,
  Shield,
  CheckCircle2,
  Car,
  MapPin,
  Users,
  Play,
  BadgeCheck,
  Clock,
  Star,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { PLATFORM_STATS } from '@/lib/constants'
import { Input } from '@/components/ui/input'

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1], delay },
  }),
}

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
}

function HeroFloatingCard() {
  return (
    <div className="hidden lg:flex flex-col items-center justify-center relative">
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
        className="w-72 rounded-2xl bg-white shadow-2xl overflow-hidden"
      >
        <div className="bg-gradient-to-br from-brand-600 to-teal-600 px-5 pt-5 pb-9 relative">
          <div className="absolute bottom-3 right-4 flex items-center gap-1 bg-black/20 rounded-full px-2.5 py-1">
            <Star className="h-3 w-3 fill-orange-300 text-orange-300" />
            <span className="text-xs font-bold text-white">4.9</span>
          </div>
        </div>
        <div className="-mt-7 px-5">
          <div className="flex items-end justify-between">
            <div className="h-14 w-14 rounded-full bg-brand-100 ring-4 ring-white flex items-center justify-center text-brand-700 font-bold text-lg shadow-md">CS</div>
            <div className="pb-1 text-right">
              <p className="text-[10px] text-gray-400">por hora</p>
              <p className="text-xl font-extrabold text-brand-700">R$180</p>
            </div>
          </div>
        </div>
        <div className="px-5 pt-3 pb-5">
          <p className="font-bold text-gray-900 text-sm">Carlos Silva</p>
          <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
            <MapPin className="h-3 w-3 text-teal-500" /> São Paulo, SP
          </p>
          <div className="mt-3 flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] bg-teal-50 text-teal-700 rounded-full px-2 py-0.5 font-medium border border-teal-100">Iniciantes</span>
            <span className="text-[10px] bg-brand-50 text-brand-700 rounded-full px-2 py-0.5 font-medium border border-brand-100">CNH B</span>
            <span className="text-[10px] text-gray-400 px-1">843 aulas</span>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-1.5">
            <BadgeCheck className="h-3.5 w-3.5 text-teal-500 shrink-0" />
            <span className="text-xs text-gray-500">Instrutor verificado</span>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.9, type: 'spring', stiffness: 300, damping: 20 }}
        className="absolute -top-2 -right-4 bg-teal-500 text-white rounded-xl px-3.5 py-2 shadow-lg text-xs font-semibold flex items-center gap-1.5"
      >
        <CheckCircle2 className="h-3.5 w-3.5" />
        Aula confirmada!
      </motion.div>

      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.2, type: 'spring', stiffness: 280, damping: 22 }}
        className="absolute -bottom-4 -left-6 bg-white rounded-xl px-3.5 py-2.5 shadow-card text-xs font-medium text-gray-700 flex items-center gap-2 border border-border/60"
      >
        <div className="h-7 w-7 rounded-lg bg-orange-100 flex items-center justify-center shrink-0">
          <Clock className="h-3.5 w-3.5 text-orange-500" />
        </div>
        <div>
          <p className="font-semibold text-gray-900 leading-none">Amanhã, 10h00</p>
          <p className="text-gray-400 leading-none mt-0.5">Próxima aula</p>
        </div>
      </motion.div>
    </div>
  )
}

export function LandingHero() {
  const navigate = useNavigate()
  const [citySearch, setCitySearch] = useState('')

  function handleCitySearch(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = citySearch.trim()
    navigate(trimmed ? `/instrutores?city=${encodeURIComponent(trimmed)}` : '/instrutores')
  }

  return (
    <section className="relative overflow-hidden -mt-[112px] bg-gradient-to-br from-brand-950 via-brand-900 to-teal-900 text-white">
      <div className="absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)', backgroundSize: '60px 60px' }}
      />
      <div className="absolute top-0 right-1/4 h-96 w-96 rounded-full bg-brand-500/20 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 h-80 w-80 rounded-full bg-teal-600/20 blur-3xl pointer-events-none" />

      <div className="relative mx-auto max-w-6xl px-4 pt-[124px] pb-20 sm:pt-[132px] sm:pb-24 lg:pt-[140px] lg:pb-28">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="text-center lg:text-left">

            <motion.h1 variants={fadeUp} custom={0.1} className="text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl text-balance">
              Aprenda a dirigir com os <span className="text-teal-300">melhores instrutores</span> do Brasil
            </motion.h1>

            <motion.p variants={fadeUp} custom={0.2} className="mt-5 text-lg text-white/75 sm:text-xl leading-relaxed max-w-xl">
              Encontre instrutores particulares qualificados, agende aulas práticas e pague com segurança — tudo em um só lugar.
            </motion.p>

            <motion.div variants={fadeUp} custom={0.3} className="mt-8 flex flex-col items-center gap-3 sm:flex-row lg:justify-start">
              <Button asChild size="lg" variant="orange" className="w-full sm:w-auto text-base px-8 h-12 shadow-orange font-semibold">
                <Link to="/instrutores">
                  <Search className="h-4 w-4" />
                  Encontrar instrutor
                </Link>
              </Button>
              <Button asChild size="lg" className="w-full sm:w-auto text-base px-8 h-12 bg-white/10 border border-white/20 text-white hover:bg-white/20 hover:border-white/30 transition-all">
                <Link to="/seja-instrutor">
                  <Car className="h-4 w-4" />
                  Quero ser instrutor
                </Link>
              </Button>
            </motion.div>

            <motion.div variants={fadeUp} custom={0.4} className="mt-6 flex flex-wrap justify-center lg:justify-start gap-x-6 gap-y-2">
              {[
                { icon: <Users className="h-3.5 w-3.5" />, text: `+${PLATFORM_STATS.instructors} instrutores` },
                { icon: <Shield className="h-3.5 w-3.5" />, text: 'Pagamento seguro' },
                { icon: <CheckCircle2 className="h-3.5 w-3.5" />, text: 'Avaliações verificadas' },
              ].map((badge) => (
                <span key={badge.text} className="flex items-center gap-1.5 text-sm text-white/65">
                  <span className="text-teal-400">{badge.icon}</span>
                  {badge.text}
                </span>
              ))}
            </motion.div>

            <motion.form variants={fadeUp} custom={0.5} onSubmit={handleCitySearch} className="mt-10 mx-auto lg:mx-0 max-w-md">
              <div className="flex overflow-hidden rounded-xl shadow-2xl shadow-black/30 ring-1 ring-white/15">
                <div className="relative flex-1">
                  <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  <Input
                    type="text"
                    placeholder="Digite sua cidade..."
                    value={citySearch}
                    onChange={(e) => setCitySearch(e.target.value)}
                    className="h-12 pl-10 pr-4 border-0 rounded-none bg-white text-gray-900 placeholder:text-gray-400 focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                </div>
                <Button type="submit" variant="orange" className="rounded-none h-12 px-6 font-semibold shrink-0">
                  Buscar
                </Button>
              </div>
            </motion.form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.92, x: 20 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <HeroFloatingCard />
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none pointer-events-none" aria-hidden>
        <svg viewBox="0 0 1440 48" xmlns="http://www.w3.org/2000/svg" className="block w-full">
          <path d="M0 48L1440 48L1440 16Q1080 48 720 24Q360 0 0 32Z" fill="white" />
        </svg>
      </div>

      {/* Play icon imported to keep import set stable */}
      <span className="hidden"><Play /></span>
    </section>
  )
}
